import { customElement, property } from 'lit/decorators.js';
import { ExtBaseElement } from '../../core/ExtBaseElement';
import './ext-button.css';

export type ExtButtonVariant = 'primary' | 'secondary' | 'danger' | 'success' | 'ghost';
export type ExtButtonSize = 'sm' | 'md' | 'lg';
export type ExtButtonType = 'button' | 'submit' | 'reset';

/**
 * <ext-button>
 *
 * Componente base de boton. Light DOM: los estilos viven en ext-button.css
 * y se seleccionan via attribute selectors (ext-button[variant="primary"]).
 *
 * === Estrategia Light DOM (UI-MONO-3a) ===
 *
 * Light DOM NO distribuye children via <slot>. El elemento <slot> dentro del
 * template de Lit solo hace slot-projection dentro de Shadow DOM; en Light
 * DOM aparece como un nodo HTML inerte y ademas el render() de Lit sobreescribe
 * el contenido original del host (this.innerHTML es reemplazado cuando
 * createRenderRoot() devuelve this).
 *
 * Resultado previo (bug): el texto <ext-button>Probar toast</ext-button> se
 * perdia apenas Lit montaba el template interno, y ademas quedaba un <button>
 * anidado con min-heights chocando contra el host inline-block (boton chiquito,
 * cuadrado, texto invisible).
 *
 * Solucion adoptada: el <ext-button> ES el boton. No renderizamos template
 * alguno: override render() como no-op y manejamos todo via:
 *   - atributos ARIA y tabindex en connectedCallback.
 *   - <span class="ext-button__leading"> inyectado por updated() para spinner/icon.
 *   - CSS sobre ext-button[variant=...] directamente (el host es el display:inline-flex).
 *
 * Esto es el patron ortodoxo Web Component + Light DOM: el custom element
 * se comporta como el button semanticamente y visualmente, y los children
 * (texto, inline elements) quedan intactos en el flujo natural del DOM.
 *
 * Convencion para futuros componentes del kit que necesitan proyectar
 * children: NO usar <slot>. Dejar los children en el Light DOM del host y
 * solo agregar adornos (icon, spinner, close button) via nodos adyacentes
 * manejados en connectedCallback/updated. Si el componente no tiene adornos
 * y solo aplica estilos (ej. chip), puede usar render() { return nothing; }.
 *
 * Props (kebab-case en HTML, camelCase en TS):
 *  - variant: primary | secondary | danger | success | ghost (default primary)
 *  - size:    sm | md | lg (default md)
 *  - loading: boolean (muestra spinner + bloquea click)
 *  - disabled: boolean
 *  - icon:    string (nombre lucide, opcional)
 *  - type:    button | submit | reset (default button)
 *
 * Content: texto o cualquier inline child del <ext-button>.
 *
 * Eventos: click nativo (bubbles=true). Si loading/disabled: preventDefault
 * + stopImmediatePropagation.
 */
@customElement('ext-button')
export class ExtButton extends ExtBaseElement {
  @property({ type: String, reflect: true }) variant: ExtButtonVariant = 'primary';
  @property({ type: String, reflect: true }) size: ExtButtonSize = 'md';
  @property({ type: Boolean, reflect: true }) loading = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: String }) icon = '';
  @property({ type: String }) type: ExtButtonType = 'button';

  private _onClick = (ev: MouseEvent): void => {
    if (this.loading || this.disabled) {
      ev.preventDefault();
      ev.stopImmediatePropagation();
    }
  };

  private _onKeyDown = (ev: KeyboardEvent): void => {
    if (this.loading || this.disabled) return;
    // Replicar semantica de <button>: Enter y Space disparan click.
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      this.click();
    }
  };

  connectedCallback(): void {
    super.connectedCallback();
    this.setAttribute('role', 'button');
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', this.disabled ? '-1' : '0');
    }
    this.addEventListener('click', this._onClick);
    this.addEventListener('keydown', this._onKeyDown);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('keydown', this._onKeyDown);
  }

  /**
   * Override: NO renderizar template Lit. En Light DOM, el render() de Lit
   * reemplaza los children del host. Dejamos a los hijos originales en paz.
   */
  protected createRenderRoot(): HTMLElement {
    return this;
  }

  render(): null {
    // No-op: el contenido lo provee el autor del HTML dentro del <ext-button>.
    return null;
  }

  /**
   * Reacciona a cambios de props para:
   *  - Sincronizar aria-busy, aria-disabled, tabindex.
   *  - Inyectar/remover el spinner de loading (prefix del contenido).
   */
  protected updated(changed: Map<string, unknown>): void {
    if (changed.has('loading')) {
      this.setAttribute('aria-busy', this.loading ? 'true' : 'false');
      this._syncLeading();
    }
    if (changed.has('disabled')) {
      if (this.disabled) {
        this.setAttribute('aria-disabled', 'true');
        this.setAttribute('tabindex', '-1');
      } else {
        this.removeAttribute('aria-disabled');
        this.setAttribute('tabindex', '0');
      }
    }
    if (changed.has('icon')) {
      this._syncLeading();
    }
  }

  /**
   * Mantiene un unico nodo leading (spinner o icon) como primer child.
   * Si loading: spinner. Si no y hay icon: <ext-icon>. Sino: ningun leading.
   */
  private _syncLeading(): void {
    const existing = this.querySelector(':scope > .ext-button__leading');
    if (existing) existing.remove();

    let leading: HTMLElement | null = null;
    if (this.loading) {
      leading = document.createElement('span');
      leading.className = 'ext-button__leading ext-button__spinner';
      leading.setAttribute('aria-hidden', 'true');
    } else if (this.icon) {
      leading = document.createElement('ext-icon');
      leading.classList.add('ext-button__leading');
      leading.setAttribute('name', this.icon);
      leading.setAttribute('size', 'sm');
      leading.setAttribute('aria-hidden', 'true');
    }
    if (leading) {
      this.insertBefore(leading, this.firstChild);
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ext-button': ExtButton;
  }
}
